import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    DESIGN_LIVE_REVIEW_METHOD,
    bugtracker,
    hasCollaborationMethodOption,
    mergeServiceProductOptions,
} from '@99designs/design-services-common';
import {
    ProductOptionsDocument,
    ServiceOptionsUpdatedDocument,
} from '@99designs/feature-design-services-brief';
import type {
    ProductOptionsQuery,
    ProductOptionsQueryVariables,
    ServiceOptionsUpdatedMutation,
    ServiceOptionsUpdatedMutationVariables,
} from '@99designs/feature-design-services-brief';
import type { BriefDocument } from '../types/chat';

export interface UseRequestTypeSyncOptions {
    document: BriefDocument | null;
    requestId?: string;
    serviceProductKey?: string;
    serviceProductVersion?: number;
    serviceProductOptions?: Array<{ key: string; value: string }>;
}

/**
 * Watches for `requestType` changes in the chat document and fires
 * `addEvent_ServiceOptionsUpdated` to keep service options in sync with
 * the value Claude returns.
 *
 * Mirrors the logic in `useUpdateGridChoiceField` from
 * `feature-design-services-brief` but driven by the chat document instead
 * of a form field mutation response.
 *
 * Known limitation: if the product doesn't have a CollaborationMethodOption
 * the mutation is skipped silently. This matches the form-field behaviour.
 */
export function useRequestTypeSync({
    document,
    requestId,
    serviceProductKey,
    serviceProductVersion,
    serviceProductOptions = [],
}: UseRequestTypeSyncOptions): void {
    const client = useApolloClient();
    const prevRequestTypeRef = useRef<string | null | undefined>(undefined);

    const currentRequestType = document?.values.find((v) => v.key === 'requestType')?.choice;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (currentRequestType === prevRequestTypeRef.current) return;
        prevRequestTypeRef.current = currentRequestType;

        if (
            !requestId ||
            !currentRequestType ||
            !serviceProductKey ||
            serviceProductVersion == null
        ) {
            return;
        }

        // Capture closure values at the time of the requestType change
        const frozenRequestId = requestId;
        const frozenKey = serviceProductKey;
        const frozenVersion = serviceProductVersion;
        const frozenOptions = serviceProductOptions;
        const frozenRequestType = currentRequestType;

        void (async () => {
            try {
                const { data } = await client.query<
                    ProductOptionsQuery,
                    ProductOptionsQueryVariables
                >({
                    query: ProductOptionsDocument,
                    variables: { key: frozenKey, version: frozenVersion },
                });

                const productOptions = data?.productOptions ?? [];

                // If the product doesn't support CollaborationMethodOption, skip silently.
                // The chat brief may be available for products that don't yet have
                // DesignerGroup/CollaborationMethod options configured.
                if (!hasCollaborationMethodOption(productOptions)) {
                    return;
                }

                const isDesignLive = frozenRequestType === DESIGN_LIVE_REVIEW_METHOD;
                const merged = mergeServiceProductOptions(
                    productOptions,
                    frozenOptions,
                    isDesignLive
                );

                if (merged.length === 0) return;

                await client.mutate<
                    ServiceOptionsUpdatedMutation,
                    ServiceOptionsUpdatedMutationVariables
                >({
                    mutation: ServiceOptionsUpdatedDocument,
                    variables: {
                        requestId: frozenRequestId,
                        input: { options: merged },
                    },
                });
            } catch (err) {
                bugtracker.notify(
                    err instanceof Error
                        ? err
                        : new Error(
                              `useRequestTypeSync: ServiceOptionsUpdated failed: ${String(err)}`
                          )
                );
            }
        })();
        // Intentionally only dep on currentRequestType — we want the mutation to fire
        // exactly once per requestType change, using values current at that render.
    }, [currentRequestType]); // eslint-disable-line react-hooks/exhaustive-deps
}
